import React from 'react';
import { Row, Col, Nav, NavItem, NavLink, TabContent, Input, Label, Button } from 'reactstrap';
import classnames from 'classnames';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { usePaymentForm } from '../hooks/usePaymentForm';
import { PAYMENT_METHOD_TABS } from '../constants';
import { formatCurrency } from '../../../../utils/format';

/**
 * Formulário de pagamento extraído do SalesSelectionPanel.
 * Gerencia método, adquirente, bandeira, parcelas e valor.
 */
const PaymentForm = ({ acquirers = [], suggestedValue = 0, onAddPayment }) => {
    const {
        paymentMethod,
        setPaymentMethod,
        paymentData,
        handleInputChange,
        availableBrands,
        estimatedFee,
        netValueDisplay,
        handleAddPaymentClick,
    } = usePaymentForm({ acquirers, suggestedValue, onAddPayment });

    const isCard = paymentMethod === PAYMENT_METHODS.CREDIT_CARD || paymentMethod === PAYMENT_METHODS.DEBIT_CARD;

    return (
        <div className="p-3 bg-light rounded-3 border border-light-subtle mt-3">
            <h6 className="text-dark fw-bold mb-3 d-flex align-items-center">
                <i className="mdi mdi-cash-register me-2 text-primary font-size-18"></i>
                Confirmar Pagamento
            </h6>

            {/* Seletor de Método */}
            <Nav pills className="nav-justified bg-white rounded-pill p-1 mb-3 shadow-sm">
                {PAYMENT_METHOD_TABS.map((method) => (
                    <NavItem key={method.id}>
                        <NavLink
                            className={classnames({ active: paymentMethod === method.id, 'rounded-pill': true, 'active-payment': paymentMethod === method.id })}
                            onClick={() => setPaymentMethod(method.id)}
                            style={{ cursor: 'pointer', padding: '8px' }}
                        >
                            <i className={`mdi ${method.icon} font-size-14 me-1`}></i>
                            <span className="font-size-11 fw-bold">{method.label}</span>
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            <TabContent activeTab={paymentMethod}>
                {/* Campos específicos para Cartão */}
                {isCard && (
                    <Row className="g-2 font-size-12">
                        <Col md={3}>
                            <Label className="mb-1">Máquina</Label>
                            <Input
                                type="select"
                                className="form-select form-select-sm"
                                value={paymentData.provider || ''}
                                onChange={(e) => handleInputChange('provider', e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {acquirers.map(acq => (
                                    <option key={acq.id} value={acq.id}>{acq.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3}>
                            <Label className="mb-1">Bandeira</Label>
                            <Input
                                type="select"
                                className="form-select form-select-sm"
                                value={paymentData.brand || ''}
                                onChange={(e) => handleInputChange('brand', e.target.value)}
                                disabled={!paymentData.provider}
                            >
                                <option value="">Selecione...</option>
                                {availableBrands.map(b => (
                                    <option key={b} value={b}>{b.toUpperCase()}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3}>
                            <Label className="mb-1">Autorização</Label>
                            <Input
                                type="text"
                                placeholder="Nº"
                                className="form-control form-control-sm"
                                value={paymentData.auth || ''}
                                onChange={(e) => handleInputChange('auth', e.target.value)}
                            />
                        </Col>
                        {paymentMethod === PAYMENT_METHODS.CREDIT_CARD && (
                            <Col md={3}>
                                <Label className="mb-1">Parcelas</Label>
                                <Input
                                    type="select"
                                    className="form-select form-select-sm"
                                    value={paymentData.installments || '1'}
                                    onChange={(e) => handleInputChange('installments', e.target.value)}
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>{i + 1}x</option>
                                    ))}
                                </Input>
                            </Col>
                        )}
                    </Row>
                )}

                {/* Campo de Valor */}
                <Row className="mt-2">
                    <Col md={12}>
                        <Label className="font-size-11 fw-bold text-muted text-uppercase mb-1">Valor do Pagamento</Label>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <Input
                                    type="number"
                                    placeholder="0,00"
                                    className="form-control fw-bold"
                                    value={paymentData.value || ''}
                                    onChange={(e) => handleInputChange('value', e.target.value)}
                                />
                                {estimatedFee !== null && (
                                    <small className="text-muted d-block mt-1">
                                        Taxa: {estimatedFee}% | Líquido:{' '}
                                        <span className="text-success fw-bold">{formatCurrency(netValueDisplay)}</span>
                                    </small>
                                )}
                            </div>
                            <Button
                                color="primary"
                                className="fw-bold px-4"
                                onClick={handleAddPaymentClick}
                                disabled={!paymentData.value && paymentData.value !== 0 && paymentData.value !== '0'}
                            >
                                ADICIONAR
                            </Button>
                        </div>
                    </Col>
                </Row>
            </TabContent>

            <style>{`
                .active-payment { background-color: var(--bs-primary) !important; color: white !important; }
            `}</style>
        </div>
    );
};

export default PaymentForm;
